import React, { useMemo } from 'react';
import cn from 'classnames';

import { ReactComponent as Arrow } from 'assets/icons/CaretPainted.svg';

import styles from './Pagination.module.scss';

const range = (start: number, end: number) => {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
};

const DOTS = '...';
const SIBLINGS = 3;
const BASE_PAGE_NUMBERS = 5;

type PaginationProps = {
  onPageChange: (page: number) => void;
  totalPageCount: number;
  siblingCount?: number;
  currentPage: number;
};
const Pagination = ({ onPageChange, currentPage, siblingCount = 2, totalPageCount }: PaginationProps) => {
  const paginationRange = useMemo(() => {
    const totalPageNumbers = siblingCount + BASE_PAGE_NUMBERS;

    if (totalPageNumbers >= totalPageCount) {
      return range(0, totalPageCount - 1);
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPageCount);

    const shouldShowLeftDots = leftSiblingIndex > 2;
    const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2;

    const firstPageIndex = 1;
    const lastPageIndex = totalPageCount;

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = SIBLINGS + 2 * siblingCount;
      const leftRange = range(1, leftItemCount);

      return [...leftRange, DOTS, totalPageCount];
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = SIBLINGS + 2 * siblingCount;
      const rightRange = range(totalPageCount - rightItemCount + 1, totalPageCount);
      return [firstPageIndex, DOTS, ...rightRange];
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      const middleRange = range(leftSiblingIndex, rightSiblingIndex);
      return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
    }
  }, [totalPageCount, siblingCount, currentPage]);

  const onNext = () => {
    if (currentPage !== lastPage) onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    if (currentPage !== 0) onPageChange(currentPage - 1);
  };

  if (paginationRange && paginationRange.length < 2) {
    return null;
  }
  const lastPage = paginationRange?.[paginationRange.length - 1];
  return (
    <div className={styles.pagination}>
      <Arrow
        onClick={onPrevious}
        className={cn(styles.arrow, styles.arrowLeft, { [styles.arrowDisabled]: currentPage === 0 })}
      />
      <ul>
        {paginationRange?.map((pageNumber, index) => {
          if (typeof pageNumber === 'string') {
            return (
              <li key={pageNumber + index} className='pagination-item dots'>
                &#8230;
              </li>
            );
          }

          return (
            <li
              key={pageNumber}
              className={cn('pagination-item', {
                [styles.selected]: pageNumber === currentPage,
              })}
              onClick={() => onPageChange(pageNumber)}
            >
              {pageNumber + 1}
            </li>
          );
        })}
      </ul>
      <Arrow
        onClick={onNext}
        className={cn(styles.arrow, styles.arrowRight, { [styles.arrowDisabled]: currentPage === lastPage })}
      />
    </div>
  );
};

export default Pagination;
